import { useEffect, useMemo, useState } from 'react';
import { getBotDamageValueClass } from './botDamageLayout';

const PARTS = [
  {
    key: 'head',
    overlay: 'da-cab.png',
    valueClass: getBotDamageValueClass('head', 'top-[15%]')
  },
  {
    key: 'torso',
    overlay: 'da-tor.png',
    valueClass: getBotDamageValueClass('torso', 'top-[40%]')
  },
  {
    key: 'shoulderRight',
    overlay: 'da-h-de.png',
    valueClass: getBotDamageValueClass('shoulderRight', 'top-[19%]')
  },
  {
    key: 'shoulderLeft',
    overlay: 'da-h-iz.png',
    valueClass: getBotDamageValueClass('shoulderLeft', 'top-[19%]')
  },
  {
    key: 'armRight',
    overlay: 'da-m-de.png',
    valueClass: getBotDamageValueClass('armRight', 'top-[36%]')
  },
  {
    key: 'armLeft',
    overlay: 'da-m-iz.png',
    valueClass: getBotDamageValueClass('armLeft', 'top-[36%]')
  },
  {
    key: 'legRight',
    overlay: 'da-p-de.png',
    valueClass: getBotDamageValueClass('legRight', 'top-[71%]')
  },
  {
    key: 'legLeft',
    overlay: 'da-p-iz.png',
    valueClass: getBotDamageValueClass('legLeft', 'top-[71%]')
  }
];

const DEFAULT_MAX = {
  head: 20,
  torso: 40,
  shoulderRight: 30,
  shoulderLeft: 30,
  armRight: 20,
  armLeft: 20,
  legRight: 30,
  legLeft: 30
};

const BOT_FIELD_BY_PART = {
  head: 'v_head',
  torso: 'v_torso',
  shoulderRight: 'p_shoulder_r',
  shoulderLeft: 'p_shoulder_l',
  armRight: 'v_arm_r',
  armLeft: 'v_arm_l',
  legRight: 'v_leg_r',
  legLeft: 'v_leg_l'
};

function getStaticAssetBase() {
  const apiBase = String(import.meta.env.VITE_API_URL || '').trim();
  if (!apiBase || apiBase === '/api') return '';
  return apiBase.replace(/\/api\/?$/, '');
}

function resolveAssetUrl(filename) {
  return `${getStaticAssetBase()}/img/func-dano/${filename}`;
}

function normalizePart(bot, override, key) {
  const fallbackMax = DEFAULT_MAX[key] || 0;
  const botField = BOT_FIELD_BY_PART[key];
  const legacyShoulder = Math.max(0, Math.floor(Number(bot?.p_shoulders ?? fallbackMax * 2) / 2));
  const shoulderFallback = key === 'shoulderLeft' || key === 'shoulderRight' ? legacyShoulder : fallbackMax;
  const current = Number(override?.current ?? bot?.[botField] ?? shoulderFallback);
  const max = Number(override?.max ?? fallbackMax);
  const safeMax = Number.isFinite(max) && max > 0 ? max : shoulderFallback;
  const safeCurrent = Number.isFinite(current) ? current : safeMax;
  const damage = Math.max(0, safeMax - safeCurrent);
  const damagePercent = safeMax > 0 ? Math.min(100, Math.round((damage / safeMax) * 100)) : 0;

  return {
    current: safeCurrent,
    max: safeMax,
    damage,
    damagePercent,
    hide: override?.hide === true
  };
}

function sanitizeOverlayImage(src) {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      const context = canvas.getContext('2d');

      if (!context) {
        resolve(src);
        return;
      }

      context.drawImage(image, 0, 0);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const { data } = imageData;

      for (let index = 0; index < data.length; index += 4) {
        const red = data[index];
        const green = data[index + 1];
        const blue = data[index + 2];
        const alpha = data[index + 3];

        if (alpha === 0) continue;

        const isNearWhite = red > 240 && green > 240 && blue > 240;
        const isVeryDark = red < 15 && green < 15 && blue < 15;

        if (isNearWhite) {
          data[index + 3] = 0;
          continue;
        }

        if (isVeryDark) {
          data[index + 3] = Math.min(alpha, 20);
        }
      }

      context.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };

    image.onerror = () => resolve(src);
    image.src = src;
  });
}

export default function BotDamagePanel({
  bot,
  title = 'Estado de daños',
  className = '',
  partOverrides = {},
  compact = false,
  open = true,
  onToggle = null
}) {
  const [overlayUrls, setOverlayUrls] = useState({});

  const parts = useMemo(() => PARTS.map((part) => {
    const override = partOverrides?.[part.key] || {};
    const normalized = normalizePart(bot, override, part.key);
    return { ...part, ...normalized, overlay: override?.overlay || part.overlay };
  }), [bot, partOverrides]);

  useEffect(() => {
    let cancelled = false;

    Promise.all(parts.map(async (part) => {
      const overlay = resolveAssetUrl(part.overlay);
      const sanitized = await sanitizeOverlayImage(overlay);
      return [part.key, sanitized];
    })).then((entries) => {
      if (cancelled) return;
      setOverlayUrls(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [parts]);

  const shellPadding = compact ? 'p-2.5' : 'p-4';
  const imageWrapPadding = compact ? 'px-2 py-2' : 'px-3 py-4';
  const imageMaxWidth = compact ? 'max-w-[250px]' : 'max-w-[420px]';
  const closedBadgeClass = 'inline-flex w-fit items-center gap-2 rounded-full border border-rose-500/20 bg-[#08101f]/95 px-2 py-1 shadow-[0_0_18px_rgba(248,113,113,0.06)]';

  return (
    <section className={`${open ? `rounded-xl border border-cyan-500/30 bg-[#08101f] ${shellPadding}` : closedBadgeClass} ${className}`}>
      <div className={open ? 'flex items-center justify-end' : 'flex items-center gap-2'}>
        <button
          type="button"
          onClick={onToggle}
          className={`inline-flex items-center justify-center rounded-full border border-rose-400/40 bg-black/65 text-[11px] leading-none text-rose-100 backdrop-blur hover:bg-rose-500/20 ${
            open ? 'h-6 w-6' : 'h-5 w-5 shrink-0'
          }`}
          title={open ? 'Cerrar daños' : 'Abrir daños'}
          aria-label={open ? 'Cerrar daños' : 'Abrir daños'}
        >
          {open ? '-' : '+'}
        </button>
        {!open && <span className="text-[10px] font-medium text-rose-100/80">Daños</span>}
      </div>

      {open ? (
        <div className={`mt-2 relative mx-auto flex w-full ${imageMaxWidth} items-center justify-center rounded-2xl border border-slate-700 bg-[#04060d] ${imageWrapPadding} shadow-[0_0_30px_rgba(0,255,255,0.08)]`}>
          <div className={`relative aspect-[451/453] w-full ${compact ? 'max-w-[220px]' : ''}`}>
            <img
              src={resolveAssetUrl('bot-fo.png')}
              alt="BOT base"
              className="absolute inset-0 h-full w-full select-none pointer-events-none object-contain"
              draggable="false"
            />

            <div className="absolute left-[12%] top-[5%] z-20 rounded-full border border-rose-500/60 bg-[#2a0910]/90 px-3 py-1 text-[11px] font-bold text-rose-300 shadow-lg">
              Nvl {Number(bot?.level || 1)}
            </div>
            <div className="absolute right-[12%] top-[5%] z-20 rounded-full border border-rose-500/60 bg-[#2a0910]/90 px-3 py-1 text-[11px] font-bold text-rose-300 shadow-lg">
              XP {Number(bot?.experience || 0)}
            </div>

            {parts.map((part) => {
              if (part.hide || part.max <= 0) return null;
              const shouldOverlay = part.damage > 0;

              return (
                <div key={part.key} className="absolute inset-0">
                  {shouldOverlay && overlayUrls[part.key] && (
                    <img
                      src={overlayUrls[part.key]}
                      alt=""
                      className="absolute inset-0 z-10 h-full w-full select-none pointer-events-none object-contain"
                      style={{ opacity: Math.max(0.72, Math.min(1, 0.7 + part.damagePercent / 100)) }}
                      draggable="false"
                    />
                  )}
                </div>
              );
            })}

            {parts.map((part) => {
              if (part.hide || part.max <= 0) return null;

              return (
                <div
                  key={`${part.key}-label`}
                  className={`absolute z-20 ${part.valueClass} rounded-full border px-2 py-1 text-[10px] font-semibold shadow-lg backdrop-blur-sm ${
                    part.damage > 0
                      ? 'border-rose-400/70 bg-rose-500/30 text-rose-100'
                      : 'border-cyan-400/40 bg-cyan-500/15 text-cyan-100'
                  } ${compact ? 'scale-90' : ''}`}
                >
                  {part.current}/{part.max}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <span className="sr-only">Daños ocultos</span>
      )}
    </section>
  );
}
